// src/pages/HireMe.jsx - Modified to use server-side OG generation
// Social media scrapers get OG tags from API, not from client-side Helmet

import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  FiSend,
  FiUser,
  FiMail,
  FiBriefcase,
  FiDollarSign,
  FiArrowRight,
  FiCheckCircle,
  FiClock,
  FiMessageCircle,
  FiCalendar,
  FiFileText,
  FiAward,
  FiHeart,
  FiZap,
  FiStar,
  FiUsers,
  FiSmile,
  FiTrendingUp,
  FiShield,
  FiGift,
  FiThumbsUp,
  FiTarget,
  FiTool,
  FiHeadphones,
  FiCoffee,
  FiEdit3,
  FiGlobe,
  FiLock,
  FiRefreshCw,
  FiAlertCircle,
  FiPlus,
  FiMinus,
  FiType,
  FiInfo,
  FiBookOpen,
  FiExternalLink,
  FiAlertTriangle,
  FiCheck,
} from 'react-icons/fi';
import { TbBrandFiverr } from 'react-icons/tb';
import { NavLink } from 'react-router-dom';
import hireService from '../services/hireService';
import { FiLoader } from 'react-icons/fi';
import { TfiRocket } from 'react-icons/tfi';
import { EmailValidationInput } from '../utils/emailValidation.jsx';
import ReviewsSection from '../components/ReviewsSection.jsx';
import StatsSection from '../components/StatsSection';
import { FiverrCard } from '../components/FiverrCard';
import { motion, AnimatePresence } from 'framer-motion';
// ✅ REMOVED: ogImageService - No client-side OG generation
// import { ogImageService } from '../services/ogImageService';

// =============================================
// GLASSY DIVIDER
// =============================================
const GlassyDivider = () => {
  return (
    <div className="relative py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="relative h-px w-full overflow-hidden">
          <motion.div
            animate={{
              x: ['-100%', '100%'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute inset-0 w-1/3"
            style={{
              background:
                'linear-gradient(to right, transparent, var(--text), transparent)',
              opacity: 0.08,
            }}
          />
        </div>
      </div>
    </div>
  );
};

// =============================================
// GLASSY CARD
// =============================================
const GlassyCard = ({ children, className = '' }) => {
  return (
    <div
      className={`relative overflow-hidden backdrop-blur-xl ${className}`}
      style={{
        backgroundColor: 'rgba(var(--bg-rgb), 0.3)',
        border: '1px solid rgba(var(--text-rgb), 0.06)',
        borderRadius: '16px',
        boxShadow:
          '0 4px 24px rgba(0, 0, 0, 0.02), inset 0 1px 0 rgba(255, 255, 255, 0.03)',
      }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-white/3 via-transparent to-transparent" />
        <div className="absolute -inset-full w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -translate-x-full animate-[shimmer_8s_infinite]" />
      </div>
      {children}
    </div>
  );
};

// =============================================
// LIQUID GRADIENT HERO - Fixed Button Flickering
// =============================================
const HireHero = () => {
  const [isHoveringHire, setIsHoveringHire] = useState(false);
  const [isHoveringBenefits, setIsHoveringBenefits] = useState(false);

  return (
    <div className="relative mb-8 overflow-hidden rounded-3xl">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 dark:from-blue-500/10 dark:via-transparent dark:to-purple-500/10" />

      <motion.div
        animate={{
          x: ['-20%', '15%', '-5%', '20%', '-20%'],
          y: ['-15%', '10%', '-10%', '15%', '-15%'],
          scale: [1, 1.3, 0.9, 1.2, 1],
          borderRadius: [
            '60% 40% 30% 70%',
            '40% 60% 70% 30%',
            '30% 70% 60% 40%',
            '70% 30% 40% 60%',
            '60% 40% 30% 70%',
          ],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute top-5 left-5 w-72 h-72 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #3b82f6 0%, #60a5fa 40%, transparent 70%)',
          opacity: 0.1,
        }}
      />

      <motion.div
        animate={{
          x: ['15%', '-15%', '10%', '-10%', '15%'],
          y: ['15%', '-10%', '20%', '-15%', '15%'],
          scale: [1.2, 0.9, 1.4, 0.8, 1.2],
          borderRadius: [
            '40% 60% 70% 30%',
            '60% 40% 30% 70%',
            '70% 30% 40% 60%',
            '30% 70% 60% 40%',
            '40% 60% 70% 30%',
          ],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute bottom-5 right-5 w-80 h-80 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #8b5cf6 0%, #a78bfa 40%, transparent 70%)',
          opacity: 0.08,
        }}
      />

      <motion.div
        animate={{
          x: ['-5%', '10%', '-10%', '5%', '-5%'],
          y: ['10%', '-15%', '5%', '-10%', '10%'],
          scale: [1, 1.2, 0.8, 1.3, 1],
          borderRadius: [
            '50% 30% 60% 40%',
            '30% 60% 40% 70%',
            '60% 40% 70% 30%',
            '40% 70% 30% 60%',
            '50% 30% 60% 40%',
          ],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #06b6d4 0%, #22d3ee 40%, transparent 70%)',
          opacity: 0.06,
        }}
      />

      <motion.div
        animate={{
          x: ['10%', '-20%', '15%', '-5%', '10%'],
          y: ['-5%', '15%', '-15%', '10%', '-5%'],
          scale: [0.8, 1.4, 0.9, 1.1, 0.8],
          borderRadius: [
            '70% 30% 50% 50%',
            '30% 70% 50% 50%',
            '50% 50% 70% 30%',
            '50% 50% 30% 70%',
            '70% 30% 50% 50%',
          ],
        }}
        transition={{
          duration: 22,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute bottom-20 left-20 w-60 h-60 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #ec4899 0%, #f472b6 40%, transparent 70%)',
          opacity: 0.05,
        }}
      />

      <div className="relative px-6 py-10 md:py-14 lg:py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 text-sm backdrop-blur-sm"
          style={{
            backgroundColor: 'rgba(var(--bg-rgb), 0.5)',
            border: '1px solid rgba(var(--text-rgb), 0.06)',
          }}
        >
          <FiBookOpen className="w-4 h-4" style={{ color: '#3b82f6' }} />
          <span style={{ color: 'var(--muted)' }}>Let's Work Together</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold mb-3 tracking-tight leading-[1.1]"
          style={{ color: 'var(--text)' }}
        >
          Ready to bring your
          <span className="block" style={{ color: 'var(--muted)' }}>
            ideas to life?
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-sm md:text-base max-w-2xl mx-auto leading-relaxed"
          style={{ color: 'var(--muted)', opacity: 0.8 }}
        >
          I'm currently available for freelance and contract work. Let's create
          something extraordinary together.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-6 flex items-center justify-center gap-3"
        >
          <span
            className="w-12 h-px"
            style={{ backgroundColor: 'var(--border)' }}
          />
          <span
            className="text-xs tracking-widest uppercase"
            style={{ color: 'var(--muted)', opacity: 0.3 }}
          >
            Hire
          </span>
          <span
            className="w-12 h-px"
            style={{ backgroundColor: 'var(--border)' }}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="mt-8 flex flex-wrap gap-4 justify-center"
        >
          <motion.a
            href="#hire-form"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 relative overflow-hidden"
            style={{
              backgroundColor: 'var(--text)',
              color: 'var(--bg)',
            }}
            onMouseEnter={() => setIsHoveringHire(true)}
            onMouseLeave={() => setIsHoveringHire(false)}
          >
            <span className="relative z-10 flex items-center gap-2">
              <span>Hire Me Now</span>
              <motion.span
                animate={{ x: isHoveringHire ? 4 : 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <FiArrowRight className="w-4 h-4" />
              </motion.span>
            </span>
          </motion.a>

          <motion.a
            href="#benefits"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 relative overflow-hidden"
            style={{
              border: '1px solid var(--border)',
              color: 'var(--text)',
            }}
            onMouseEnter={() => setIsHoveringBenefits(true)}
            onMouseLeave={() => setIsHoveringBenefits(false)}
          >
            <span className="relative z-10">View Benefits</span>
          </motion.a>
        </motion.div>
      </div>
    </div>
  );
};

function HireMe() {
  // ==================== FORM STATE ====================
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    budget: '',
    timeline: '',
    projectType: '',
    message: '',
    customBudget: '',
    customTimeline: '',
    customProjectType: '',
  });

  const [useCustomBudget, setUseCustomBudget] = useState(false);
  const [useCustomTimeline, setUseCustomTimeline] = useState(false);
  const [useCustomProjectType, setUseCustomProjectType] = useState(false);

  const [showEmailTooltip, setShowEmailTooltip] = useState(false);
  const [showSpamAlert, setShowSpamAlert] = useState(true);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState('professional');
  const [submitError, setSubmitError] = useState(null);
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({
    name: false,
    email: false,
    message: false,
  });

  const [emailValidation, setEmailValidation] = useState({
    isValid: false,
    errorMessage: null,
    suggestion: null,
    qualityScore: 0,
    isValidating: false,
  });

  // ==================== ANIMATION ON SCROLL ====================
  const [animatedSections, setAnimatedSections] = useState({});
  const [hoveredItem, setHoveredItem] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -100px 0px' },
    );

    const sections = document.querySelectorAll('.animate-on-scroll');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // ==================== WHY CHOOSE ME ====================
  const benefits = [
    {
      icon: FiUser,
      title: 'Personalized Approach',
      description:
        'Tailored design solutions that reflect your unique brand voice and vision',
      gradient: 'from-blue-500/20 to-blue-600/10',
    },
    {
      icon: FiClock,
      title: 'Fast Turnaround',
      description: 'Quick delivery without compromising quality',
      gradient: 'from-purple-500/20 to-purple-600/10',
    },
    {
      icon: FiShield,
      title: 'Quality Guarantee',
      description: '100% satisfaction or money back',
      gradient: 'from-green-500/20 to-green-600/10',
    },
    {
      icon: FiHeadphones,
      title: 'Ongoing Support',
      description: '30 days post-project support',
      gradient: 'from-pink-500/20 to-pink-600/10',
    },
  ];

  // ==================== PROJECT TYPES ====================
  const projectTypeOptions = [
    'Brand Identity',
    'Logo Design',
    'Product Visual Design',
    'Image Tracing',
    'UI/UX Design',
    'Website Design',
    'Mobile App',
    'Packaging',
    'Motion Graphics',
    'Social Media',
    'Print Materials',
    'Illustration',
  ];

  // Budget options
  const budgetOptions = [
    { value: '1k-3k', label: '$1,000 - $3,000' },
    { value: '3k-5k', label: '$3,000 - $5,000' },
    { value: '5k-10k', label: '$5,000 - $10,000' },
    { value: '10k-15k', label: '$10,000 - $15,000' },
    { value: '15k-25k', label: '$15,000 - $25,000' },
    { value: '25k+', label: '$25,000+' },
  ];

  // Timeline options
  const timelineOptions = [
    { value: '1-2 weeks', label: '1-2 weeks (Urgent)' },
    { value: '3-4 weeks', label: '3-4 weeks (Standard)' },
    { value: '1-2 months', label: '1-2 months (Extended)' },
    { value: '2-3 months', label: '2-3 months (Complex)' },
    { value: '3+ months', label: '3+ months (Long-term)' },
    { value: 'flexible', label: "Flexible - Let's discuss" },
  ];

  // ==================== VALIDATION FUNCTIONS ====================
  const validateField = (fieldName, value) => {
    switch (fieldName) {
      case 'name':
        if (!value?.trim()) return 'Name is required';
        if (value.trim().length < 2)
          return 'Name must be at least 2 characters';
        if (value.trim().length > 100)
          return 'Name is too long (max 100 characters)';
        if (!/^[a-zA-Z\s\-']+$/.test(value.trim()))
          return 'Name contains invalid characters';
        return null;
      case 'message':
        if (!value?.trim()) return 'Message is required';
        if (value.trim().length < 20)
          return 'Please provide more details (minimum 20 characters)';
        if (value.trim().length > 5000)
          return 'Message is too long (maximum 5000 characters)';
        return null;
      default:
        return null;
    }
  };

  const handleBlur = fieldName => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    const error = validateField(fieldName, formData[fieldName]);
    if (error) {
      setErrors(prev => ({ ...prev, [fieldName]: error }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleEmailValidationChange = (isValid, validationResult) => {
    setEmailValidation({
      isValid: isValid,
      errorMessage: validationResult?.reason || null,
      suggestion: validationResult?.suggestions || null,
      qualityScore: validationResult?.score || 0,
      isValidating: false,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    const nameError = validateField('name', formData.name);
    if (nameError) newErrors.name = nameError;

    if (!emailValidation.isValid) {
      newErrors.email =
        emailValidation.errorMessage || 'Please enter a valid email address';
    }

    const messageError = validateField('message', formData.message);
    if (messageError) newErrors.message = messageError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ==================== FORM HANDLERS ====================
  const handleSubmit = async e => {
    e.preventDefault();

    setTouchedFields({
      name: true,
      email: true,
      message: true,
    });

    const isValid = validateForm();

    if (isValid && emailValidation.isValid) {
      setIsSubmitting(true);
      setSubmitError(null);

      const finalBudget = useCustomBudget
        ? formData.customBudget
        : formData.budget;
      const finalTimeline = useCustomTimeline
        ? formData.customTimeline
        : formData.timeline;
      const finalProjectType = useCustomProjectType
        ? formData.customProjectType
        : formData.projectType;

      const submitData = {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        budget: finalBudget,
        timeline: finalTimeline,
        projectType: finalProjectType,
        message: formData.message,
      };

      try {
        await hireService.submitInquiry(submitData, selectedPackage);
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          company: '',
          budget: '',
          timeline: '',
          projectType: '',
          message: '',
          customBudget: '',
          customTimeline: '',
          customProjectType: '',
        });
        setSelectedPackage('professional');
        setUseCustomBudget(false);
        setUseCustomTimeline(false);
        setUseCustomProjectType(false);
        setEmailValidation({
          isValid: false,
          errorMessage: null,
          suggestion: null,
          qualityScore: 0,
          isValidating: false,
        });
        setTimeout(() => setIsSubmitted(false), 5000);
      } catch (error) {
        console.error('Error submitting inquiry:', error);
        setSubmitError(
          error.message ||
            'Failed to submit your inquiry. Please try again later.',
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const isFormValid =
    formData.name.trim() &&
    emailValidation.isValid &&
    formData.message.trim() &&
    !errors.name &&
    !errors.message;

  // ==================== BONUS OFFERS ====================
  const bonuses = [
    {
      icon: FiStar,
      title: 'Priority Delivery',
      description: 'Fast-track your project with priority scheduling',
    },
    {
      icon: FiEdit3,
      title: 'Free Mockups',
      description: 'Professional presentations included',
    },
    {
      icon: FiAward,
      title: 'Bonus Revisions',
      description: '2 extra revision rounds included',
    },
    {
      icon: FiThumbsUp,
      title: 'Satisfaction Guarantee',
      description: '100% satisfaction or full refund',
    },
  ];

  return (
    <>
      {/*
        ✅ HELMET - Only basic page info
        ❌ NO OG TAGS - Server handles OG generation via API
        Social media scrapers get OG from /api/social-preview?page=hire
      */}
      <Helmet>
        <title>Hire Me | WD Hajbul - Available for Projects</title>
        <meta
          name="description"
          content="Looking for a professional designer? Hire WD Hajbul for your next branding or design project."
        />
        <meta
          name="keywords"
          content="hire, freelance designer, brand designer, product designer, graphic designer, available for work, freelance"
        />
        <link rel="canonical" href="https://wdhejbul.com/hire" />
        <meta name="robots" content="index, follow" />

        {/* Schema for Hire page */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: 'Hire WD Hajbul - Professional Design Services',
            description:
              'Looking for a professional designer? Hire WD Hajbul for your next branding or design project.',
            provider: {
              '@type': 'Person',
              name: 'WD Hajbul',
              jobTitle: 'Brand & Product Visual Designer',
            },
            serviceType: 'Design Services',
            offers: {
              '@type': 'Offer',
              availability: 'https://schema.org/InStock',
              availableAtOrFrom: 'https://wdhejbul.com/hire',
            },
          })}
        </script>
      </Helmet>

      <div
        className="min-h-screen"
        style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
      >
        {/* ==================== HERO SECTION ==================== */}
        <section className="pt-32 pb-20 max-w-7xl mx-auto max-sm:p-5 max-sm:pt-24">
          <HireHero />
        </section>

        <StatsSection
          fetchProjectCount={true}
          columns={4}
          variant="default"
          showBorder={true}
        />

        <GlassyDivider />

        {/* ==================== BENEFITS SECTION ==================== */}
        <section
          id="benefits"
          className="animate-on-scroll py-20 px-4 max-w-6xl mx-auto transition-all duration-700 delay-200"
        >
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <FiZap className="w-4 h-4" />
              <span className="text-sm" style={{ color: 'var(--text)' }}>
                Why Choose Me
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: 'var(--text)' }}
            >
              What makes me
              <br />
              <span className="italic" style={{ color: 'var(--muted)' }}>
                different?
              </span>
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  delay: idx * 0.1,
                  duration: 0.5,
                  ease: 'easeOut',
                }}
                viewport={{ once: true }}
                className="group"
                onMouseEnter={() => setHoveredItem(`benefit-${idx}`)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <div
                  className="relative p-6 rounded-2xl transition-all duration-500 overflow-hidden"
                  style={{
                    backgroundColor: 'var(--bg)',
                    border: '1px solid var(--border)',
                  }}
                >
                  <motion.div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background: `linear-gradient(135deg, ${benefit.gradient}, transparent 70%)`,
                    }}
                  />

                  <motion.div
                    className="absolute bottom-0 left-0 h-0.5 rounded-full"
                    style={{
                      backgroundColor: 'var(--text)',
                      opacity: 0.15,
                    }}
                    initial={{ width: '0%' }}
                    whileInView={{ width: '100%' }}
                    transition={{ duration: 0.8, delay: idx * 0.1 + 0.3 }}
                    viewport={{ once: true }}
                  />

                  <div className="relative z-10 text-center">
                    <div className="relative inline-block mb-4">
                      <motion.div
                        className="absolute inset-0 rounded-full"
                        animate={{
                          scale: [1, 1.3, 1],
                          opacity: [0.2, 0, 0.2],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          delay: idx * 0.3,
                        }}
                        style={{
                          border: '2px solid var(--text)',
                          opacity: 0.05,
                        }}
                      />
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg mx-auto"
                        style={{
                          backgroundColor: 'var(--border)',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                        }}
                      >
                        <benefit.icon
                          className="w-7 h-7 transition-all duration-300 group-hover:rotate-12"
                          style={{ color: 'var(--text)' }}
                        />
                      </div>
                    </div>

                    <h3
                      className="font-semibold text-lg mb-2"
                      style={{ color: 'var(--text)' }}
                    >
                      {benefit.title}
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--muted)' }}>
                      {benefit.description}
                    </p>

                    <motion.div
                      className="w-1.5 h-1.5 rounded-full mx-auto mt-4"
                      style={{
                        backgroundColor: 'var(--text)',
                        opacity: 0.2,
                      }}
                      animate={{
                        scale: [1, 1.5, 1],
                        opacity: [0.2, 0.5, 0.2],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: idx * 0.2,
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <GlassyDivider />

        {/* ==================== BONUS OFFERS ==================== */}
        <section className="animate-on-scroll py-20 px-4 max-w-6xl mx-auto transition-all duration-700 delay-300">
          <GlassyCard className="relative rounded-3xl p-12 overflow-hidden">
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="absolute top-1/2 right-1/4 w-64 h-64 rounded-full blur-3xl"
              style={{
                background:
                  'radial-gradient(circle, var(--text) 0%, transparent 70%)',
                opacity: 0.03,
              }}
            />

            <div className="relative z-10 text-center mb-8">
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <FiGift className="w-4 h-4" />
                <span className="text-sm" style={{ color: 'var(--text)' }}>
                  Limited Time Offer
                </span>
              </div>
              <h2
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ color: 'var(--text)' }}
              >
                Exclusive Bonuses
                <br />
                <span className="italic" style={{ color: 'var(--muted)' }}>
                  When you hire me today
                </span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
              {bonuses.map((bonus, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{
                    delay: idx * 0.1,
                    duration: 0.5,
                    type: 'spring',
                    stiffness: 200,
                  }}
                  viewport={{ once: true }}
                  className="text-center group"
                >
                  <motion.div
                    className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg"
                    style={{
                      backgroundColor: 'var(--bg)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                    }}
                    whileHover={{ rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 0.6 }}
                  >
                    <bonus.icon
                      className="w-6 h-6 transition-all duration-300 group-hover:rotate-12"
                      style={{ color: 'var(--text)' }}
                    />
                  </motion.div>
                  <h3
                    className="font-semibold mb-1"
                    style={{ color: 'var(--text)' }}
                  >
                    {bonus.title}
                  </h3>
                  <p className="text-xs" style={{ color: 'var(--muted)' }}>
                    {bonus.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </GlassyCard>
        </section>

        <GlassyDivider />

        {/* ==================== TESTIMONIALS ==================== */}
        <ReviewsSection />

        <GlassyDivider />

        {/* ==================== HIRE FORM SECTION ==================== */}
        <section
          id="hire-form"
          className="animate-on-scroll py-20 px-4 max-w-6xl mx-auto transition-all duration-700 delay-500"
        >
          <div className="grid lg:grid-cols-2 gap-12">
            {/* LEFT COLUMN - FORM */}
            <div>
              <span className="text-sm uppercase tracking-wider opacity-60 mb-4 block">
                Get Started
              </span>
              <h2
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{ color: 'var(--text)' }}
              >
                Let's discuss
                <br />
                <span className="italic" style={{ color: 'var(--muted)' }}>
                  your project
                </span>
              </h2>
              <p className="mb-8" style={{ color: 'var(--muted)' }}>
                Fill out the form and I'll get back to you within 24 hours.
                Let's create something amazing together!
              </p>

              {/* ==================== SPAM ALERT BANNER ==================== */}
              {showSpamAlert && (
                <div
                  className="mb-6 p-3 rounded-lg border animate-fade-in"
                  style={{
                    backgroundColor: 'rgba(245, 158, 11, 0.06)',
                    borderColor: 'rgba(245, 158, 11, 0.2)',
                  }}
                >
                  <div className="flex items-start gap-2">
                    <FiAlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-[11px] font-medium"
                        style={{ color: '#f59e0b' }}
                      >
                        📬 Check Your Spam Folder
                      </p>
                      <p
                        className="text-[10px] leading-relaxed"
                        style={{ color: 'var(--muted)' }}
                      >
                        Our response may go to spam. Check there if you don't
                        see our reply.
                      </p>
                      <div className="flex items-start gap-1.5 mt-1 p-1.5 rounded bg-blue-500/5 border border-blue-500/10">
                        <FiMail className="w-3 h-3 text-blue-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p
                            className="text-[10px]"
                            style={{ color: 'var(--muted)' }}
                          >
                            <span className="font-medium text-blue-500">
                              Pro tip:
                            </span>{' '}
                            Add{' '}
                            <span className="text-blue-500 font-mono text-[9px]">
                              wdhejbul@gmail.com
                            </span>{' '}
                            to your contacts — this tells your email provider
                            <span className="text-[9px] block mt-0.5 text-blue-400/70">
                              (Gmail, Outlook, etc.) that we're a trusted
                              sender, so our replies go straight to your inbox,
                              not spam.
                            </span>
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowSpamAlert(false)}
                        className="text-[9px] mt-1 hover:underline opacity-60"
                        style={{ color: 'var(--muted)' }}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <AnimatePresence>
                {isSubmitted && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="mb-6 p-4 rounded-xl flex flex-col gap-1"
                    style={{
                      backgroundColor: 'rgba(34, 197, 94, 0.06)',
                      border: '1px solid rgba(34, 197, 94, 0.2)',
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span style={{ color: 'var(--text)' }}>
                        Thank you! I'll get back to you within 24 hours.
                      </span>
                    </div>
                    <p className="text-xs text-yellow-500 ml-8">
                      ⚠️ Please check your spam folder if you don't see my
                      reply!
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {submitError && (
                <div
                  className="mb-6 p-4 rounded-xl flex items-center gap-3"
                  style={{
                    backgroundColor: 'rgba(239, 68, 68, 0.05)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                  }}
                >
                  <FiAlertCircle className="w-5 h-5 text-red-500" />
                  <span className="text-sm text-red-500">{submitError}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name Field */}
                <div className="relative">
                  <div
                    className="absolute left-4 top-1/2 -translate-y-1/2"
                    style={{
                      color:
                        focusedField === 'name'
                          ? 'var(--text)'
                          : 'var(--muted)',
                    }}
                  >
                    <FiUser className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => {
                      setFocusedField(null);
                      handleBlur('name');
                    }}
                    placeholder="Your name *"
                    className="w-full pl-11 pr-4 py-3 rounded-xl outline-none transition-all duration-300"
                    style={{
                      backgroundColor: 'transparent',
                      border: `1px solid ${errors.name && touchedFields.name ? '#ef4444' : 'var(--border)'}`,
                      color: 'var(--text)',
                    }}
                  />
                  {errors.name && touchedFields.name && (
                    <div className="absolute -bottom-5 left-0 text-xs text-red-500">
                      {errors.name}
                    </div>
                  )}
                </div>

                {/* Email Field */}
                <div className="relative">
                  <div className="flex items-center gap-2 mb-1">
                    <label
                      className="text-sm font-medium"
                      style={{ color: 'var(--text)' }}
                    >
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div
                      className="relative"
                      onMouseEnter={() => setShowEmailTooltip(true)}
                      onMouseLeave={() => setShowEmailTooltip(false)}
                    >
                      <FiInfo
                        className="w-3.5 h-3.5 cursor-help"
                        style={{ color: '#3b82f6' }}
                      />
                      {showEmailTooltip && (
                        <div
                          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 rounded-lg shadow-lg z-50 animate-fade-in"
                          style={{
                            backgroundColor: 'var(--bg)',
                            border: '1px solid var(--border)',
                            color: 'var(--text)',
                          }}
                        >
                          <div className="text-xs space-y-1">
                            <p className="font-semibold">
                              Why we need your email?
                            </p>
                            <p>• We'll send confirmation of your inquiry</p>
                            <p>• Get updates about your project</p>
                            <p>• Receive our response within 24 hours</p>
                            <div
                              className="mt-1 pt-1 border-t"
                              style={{ borderColor: 'var(--border)' }}
                            >
                              <p className="text-yellow-500 text-[10px]">
                                ⚠️ Invalid emails won't receive confirmation
                              </p>
                            </div>
                          </div>
                          <div
                            className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 rotate-45"
                            style={{
                              backgroundColor: 'var(--bg)',
                              borderRight: '1px solid var(--border)',
                              borderBottom: '1px solid var(--border)',
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <EmailValidationInput
                    value={formData.email}
                    onChange={e => {
                      setFormData(prev => ({ ...prev, email: e.target.value }));
                      if (errors.email) {
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.email;
                          return newErrors;
                        });
                      }
                    }}
                    onBlur={() => {
                      setTouchedFields(prev => ({ ...prev, email: true }));
                    }}
                    placeholder="your@email.com *"
                    required
                    className="w-full"
                    onValidationChange={handleEmailValidationChange}
                  />
                </div>

                {/* Company Field */}
                <div className="relative">
                  <div
                    className="absolute left-4 top-1/2 -translate-y-1/2"
                    style={{
                      color:
                        focusedField === 'company'
                          ? 'var(--text)'
                          : 'var(--muted)',
                    }}
                  >
                    <FiBriefcase className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('company')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Company name (optional)"
                    className="w-full pl-11 pr-4 py-3 rounded-xl outline-none transition-all duration-300"
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid var(--border)',
                      color: 'var(--text)',
                    }}
                  />
                </div>

                {/* Budget Field */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label
                      className="text-sm"
                      style={{ color: 'var(--muted)' }}
                    >
                      Budget Range *
                    </label>
                    <button
                      type="button"
                      onClick={() => setUseCustomBudget(!useCustomBudget)}
                      className="text-xs flex items-center gap-1 transition-opacity hover:opacity-70"
                      style={{ color: '#3b82f6' }}
                    >
                      {useCustomBudget ? (
                        <>
                          <FiMinus className="w-3 h-3" /> Use preset options
                        </>
                      ) : (
                        <>
                          <FiPlus className="w-3 h-3" /> Enter custom budget
                        </>
                      )}
                    </button>
                  </div>

                  {!useCustomBudget ? (
                    <div className="relative">
                      <div
                        className="absolute left-4 top-1/2 -translate-y-1/2"
                        style={{ color: 'var(--muted)' }}
                      >
                        <FiDollarSign className="w-4 h-4" />
                      </div>
                      <select
                        name="budget"
                        value={formData.budget}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('budget')}
                        onBlur={() => setFocusedField(null)}
                        className="w-full pl-11 pr-4 py-3 rounded-xl outline-none transition-all duration-300 appearance-none cursor-pointer"
                        style={{
                          backgroundColor: 'transparent',
                          border: '1px solid var(--border)',
                          color: 'var(--text)',
                        }}
                      >
                        <option value="">Select budget range</option>
                        {budgetOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="relative">
                      <div
                        className="absolute left-4 top-1/2 -translate-y-1/2"
                        style={{ color: 'var(--muted)' }}
                      >
                        <FiType className="w-4 h-4" />
                      </div>
                      <input
                        type="text"
                        name="customBudget"
                        value={formData.customBudget}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('customBudget')}
                        onBlur={() => setFocusedField(null)}
                        placeholder="Enter your budget (e.g., $5,000 - $7,000)"
                        className="w-full pl-11 pr-4 py-3 rounded-xl outline-none transition-all duration-300"
                        style={{
                          backgroundColor: 'transparent',
                          border: '1px solid var(--border)',
                          color: 'var(--text)',
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Timeline Field */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label
                      className="text-sm"
                      style={{ color: 'var(--muted)' }}
                    >
                      Timeline *
                    </label>
                    <button
                      type="button"
                      onClick={() => setUseCustomTimeline(!useCustomTimeline)}
                      className="text-xs flex items-center gap-1 transition-opacity hover:opacity-70"
                      style={{ color: '#3b82f6' }}
                    >
                      {useCustomTimeline ? (
                        <>
                          <FiMinus className="w-3 h-3" /> Use preset options
                        </>
                      ) : (
                        <>
                          <FiPlus className="w-3 h-3" /> Enter custom timeline
                        </>
                      )}
                    </button>
                  </div>

                  {!useCustomTimeline ? (
                    <div className="relative">
                      <div
                        className="absolute left-4 top-1/2 -translate-y-1/2"
                        style={{ color: 'var(--muted)' }}
                      >
                        <FiCalendar className="w-4 h-4" />
                      </div>
                      <select
                        name="timeline"
                        value={formData.timeline}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('timeline')}
                        onBlur={() => setFocusedField(null)}
                        className="w-full pl-11 pr-4 py-3 rounded-xl outline-none transition-all duration-300 appearance-none cursor-pointer"
                        style={{
                          backgroundColor: 'transparent',
                          border: '1px solid var(--border)',
                          color: 'var(--text)',
                        }}
                      >
                        <option value="">Select timeline</option>
                        {timelineOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="relative">
                      <div
                        className="absolute left-4 top-1/2 -translate-y-1/2"
                        style={{ color: 'var(--muted)' }}
                      >
                        <FiType className="w-4 h-4" />
                      </div>
                      <input
                        type="text"
                        name="customTimeline"
                        value={formData.customTimeline}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('customTimeline')}
                        onBlur={() => setFocusedField(null)}
                        placeholder="Enter your timeline (e.g., Need by end of next month)"
                        className="w-full pl-11 pr-4 py-3 rounded-xl outline-none transition-all duration-300"
                        style={{
                          backgroundColor: 'transparent',
                          border: '1px solid var(--border)',
                          color: 'var(--text)',
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Project Type Field */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label
                      className="text-sm"
                      style={{ color: 'var(--muted)' }}
                    >
                      Project Type *
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        setUseCustomProjectType(!useCustomProjectType)
                      }
                      className="text-xs flex items-center gap-1 transition-opacity hover:opacity-70"
                      style={{ color: '#3b82f6' }}
                    >
                      {useCustomProjectType ? (
                        <>
                          <FiMinus className="w-3 h-3" /> Use preset options
                        </>
                      ) : (
                        <>
                          <FiPlus className="w-3 h-3" /> Enter custom project
                          type
                        </>
                      )}
                    </button>
                  </div>

                  {!useCustomProjectType ? (
                    <div className="relative">
                      <div
                        className="absolute left-4 top-1/2 -translate-y-1/2"
                        style={{ color: 'var(--muted)' }}
                      >
                        <FiTarget className="w-4 h-4" />
                      </div>
                      <select
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('projectType')}
                        onBlur={() => setFocusedField(null)}
                        className="w-full pl-11 pr-4 py-3 rounded-xl outline-none transition-all duration-300 appearance-none cursor-pointer"
                        style={{
                          backgroundColor: 'transparent',
                          border: '1px solid var(--border)',
                          color: 'var(--text)',
                        }}
                      >
                        <option value="">Select project type</option>
                        {projectTypeOptions.map(type => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="relative">
                      <div
                        className="absolute left-4 top-1/2 -translate-y-1/2"
                        style={{ color: 'var(--muted)' }}
                      >
                        <FiType className="w-4 h-4" />
                      </div>
                      <input
                        type="text"
                        name="customProjectType"
                        value={formData.customProjectType}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('customProjectType')}
                        onBlur={() => setFocusedField(null)}
                        placeholder="Enter your project type (e.g., E-commerce Website)"
                        className="w-full pl-11 pr-4 py-3 rounded-xl outline-none transition-all duration-300"
                        style={{
                          backgroundColor: 'transparent',
                          border: '1px solid var(--border)',
                          color: 'var(--text)',
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Message Field */}
                <div className="relative">
                  <div
                    className="absolute left-4 top-4"
                    style={{
                      color:
                        focusedField === 'message'
                          ? 'var(--text)'
                          : 'var(--muted)',
                    }}
                  >
                    <FiMessageCircle className="w-4 h-4" />
                  </div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('message')}
                    onBlur={() => {
                      setFocusedField(null);
                      handleBlur('message');
                    }}
                    placeholder="Tell me about your project... * (minimum 20 characters)"
                    rows="4"
                    className="w-full pl-11 pr-4 py-3 rounded-xl outline-none transition-all duration-300 resize-none"
                    style={{
                      backgroundColor: 'transparent',
                      border: `1px solid ${errors.message && touchedFields.message ? '#ef4444' : 'var(--border)'}`,
                      color: 'var(--text)',
                    }}
                  />
                  {touchedFields.message && (
                    <div
                      className="absolute bottom-3 right-3 text-xs px-2 py-0.5 rounded"
                      style={{
                        backgroundColor: 'var(--border)',
                        color: 'var(--muted)',
                      }}
                    >
                      {formData.message.length}/5000
                    </div>
                  )}
                  {errors.message && touchedFields.message && (
                    <div className="absolute -bottom-5 left-0 text-xs text-red-500">
                      {errors.message}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting || !isFormValid}
                  whileHover={isFormValid ? { scale: 1.02 } : {}}
                  whileTap={isFormValid ? { scale: 0.98 } : {}}
                  className="relative w-full py-4 rounded-xl font-medium overflow-hidden group transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: isFormValid
                      ? 'var(--text)'
                      : 'var(--border)',
                    color: isFormValid ? 'var(--bg)' : 'var(--muted)',
                  }}
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {isSubmitting ? (
                      <>
                        <FiLoader className="w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Hire Me Now
                        <FiSend className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                      </>
                    )}
                  </span>
                </motion.button>

                {/* Form Status */}
                <div
                  className="flex items-center justify-center gap-3 text-[10px]"
                  style={{ color: 'var(--muted)' }}
                >
                  <span className="flex items-center gap-1">
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${formData.name.trim() ? 'bg-green-500' : 'bg-gray-400'}`}
                    />
                    Name
                  </span>
                  <span className="flex items-center gap-1">
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${emailValidation.isValid ? 'bg-green-500' : 'bg-gray-400'}`}
                    />
                    Email
                  </span>
                  <span className="flex items-center gap-1">
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${formData.message.trim().length >= 20 ? 'bg-green-500' : 'bg-gray-400'}`}
                    />
                    Message
                  </span>
                </div>

                {/* Privacy Notice */}
                <div
                  className="flex items-center justify-between text-[9px]"
                  style={{ color: 'var(--muted)' }}
                >
                  <span className="flex items-center gap-1">
                    <FiCheck className="w-2.5 h-2.5 text-green-500" />
                    Privacy secure
                  </span>
                  <span>We'll respond within 24 hours</span>
                </div>
              </form>
            </div>

            {/* RIGHT COLUMN - INFO & GUARANTEE */}
            <div>
              {/* Process Timeline */}
              <div className="mb-8">
                <h3
                  className="text-xl font-semibold mb-4 flex items-center gap-2"
                  style={{ color: 'var(--text)' }}
                >
                  <FiRefreshCw className="w-5 h-5" />
                  My process
                </h3>
                <div className="space-y-4">
                  {[
                    {
                      step: 1,
                      title: 'Discovery Call',
                      desc: 'Understand your goals and vision',
                      gradient: 'from-blue-500/20 to-blue-600/10',
                    },
                    {
                      step: 2,
                      title: 'Proposal & Contract',
                      desc: 'Detailed scope and timeline',
                      gradient: 'from-purple-500/20 to-purple-600/10',
                    },
                    {
                      step: 3,
                      title: 'Design Phase',
                      desc: 'Creative development and iterations',
                      gradient: 'from-pink-500/20 to-pink-600/10',
                    },
                    {
                      step: 4,
                      title: 'Final Delivery',
                      desc: 'Assets delivery and support',
                      gradient: 'from-green-500/20 to-green-600/10',
                    },
                  ].map((step, idx) => (
                    <motion.div
                      key={step.step}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1, duration: 0.4 }}
                      viewport={{ once: true }}
                      className="group relative"
                      onMouseEnter={() => setHoveredItem(`step-${idx}`)}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      <GlassyCard className="p-4 rounded-xl transition-all duration-300 hover:translate-x-1">
                        <div className="flex gap-4">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg"
                            style={{
                              backgroundColor: 'var(--border)',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                              color: 'var(--text)',
                            }}
                          >
                            {step.step}
                          </div>
                          <div>
                            <div
                              className="font-semibold text-sm"
                              style={{ color: 'var(--text)' }}
                            >
                              {step.title}
                            </div>
                            <div
                              className="text-xs"
                              style={{ color: 'var(--muted)' }}
                            >
                              {step.desc}
                            </div>
                          </div>
                        </div>
                      </GlassyCard>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Guarantee Card */}
              <GlassyCard className="p-6 rounded-2xl text-center mb-8">
                <motion.div
                  animate={{
                    scale: [1, 1.05, 1],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl"
                  style={{
                    background:
                      'radial-gradient(circle, var(--text) 0%, transparent 70%)',
                    opacity: 0.02,
                  }}
                />
                <div className="relative z-10">
                  <FiThumbsUp
                    className="w-12 h-12 mx-auto mb-3"
                    style={{ color: 'var(--text)' }}
                  />
                  <h3
                    className="text-xl font-semibold mb-2"
                    style={{ color: 'var(--text)' }}
                  >
                    100% Satisfaction Guarantee
                  </h3>
                  <p className="text-sm mb-3" style={{ color: 'var(--muted)' }}>
                    Not happy with the result? I'll revise until you're
                    completely satisfied.
                  </p>
                  <div
                    className="text-xs opacity-60"
                    style={{ color: 'var(--muted)' }}
                  >
                    * Terms and conditions apply
                  </div>
                </div>
              </GlassyCard>

              {/* Availability */}
              <GlassyCard className="p-6 rounded-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <motion.div
                    className="w-3 h-3 rounded-full bg-green-500"
                    animate={{
                      scale: [1, 1.3, 1],
                      opacity: [0.5, 1, 0.5],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  />
                  <span
                    className="text-sm font-medium"
                    style={{ color: 'var(--text)' }}
                  >
                    Currently Available for Work
                  </span>
                </div>
                <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>
                  I'm currently accepting new projects. The next available slot
                  starts in 2 weeks.
                </p>
                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: 'var(--muted)' }}>Response time:</span>
                  <span
                    className="font-semibold"
                    style={{ color: 'var(--text)' }}
                  >
                    &lt; 24 hours
                  </span>
                </div>
                <p className="text-xs text-yellow-500 mt-3">
                  ⚠️ Please check your spam folder if you don't see my reply!
                </p>
              </GlassyCard>
            </div>
          </div>
        </section>

        <GlassyDivider />

        {/* ==================== FIVERR CARD ==================== */}
        <section className="py-12 px-4 max-w-7xl mx-auto">
          <FiverrCard variant="standard" />
        </section>

        <GlassyDivider />

        {/* ==================== FAQ SECTION ==================== */}
        <section className="animate-on-scroll py-20 px-4 max-w-4xl mx-auto transition-all duration-700 delay-600">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-wider opacity-60 mb-4 block">
              FAQs
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: 'var(--text)' }}
            >
              Got questions?
              <br />
              <span className="italic" style={{ color: 'var(--muted)' }}>
                I've got answers
              </span>
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'How soon can you start my project?',
                a: 'I typically start within 1-2 weeks after signing the contract and receiving the deposit.',
              },
              {
                q: "What's your payment structure?",
                a: 'I require 50% upfront and 50% upon completion. For larger projects, milestone payments are available.',
              },
              {
                q: 'Do you sign NDAs?',
                a: "Absolutely! Client privacy is important to me. I'm happy to sign NDAs before discussing project details.",
              },
              {
                q: 'What happens if I need revisions?',
                a: 'All packages include revision rounds. Unlimited revisions for Professional and Enterprise packages.',
              },
              {
                q: "I didn't receive your email. What should I do?",
                a: "First, check your spam/junk folder. If you still don't see it, please add wdhejbul@gmail.com to your contacts and ask me to resend. You can also reach me directly via phone or WhatsApp.",
              },
            ].map((faq, idx) => (
              <GlassyCard
                key={idx}
                className="p-5 rounded-xl transition-all duration-300 hover:translate-x-1 cursor-pointer"
              >
                <h3
                  className="font-semibold mb-2"
                  style={{ color: 'var(--text)' }}
                >
                  {faq.q}
                </h3>
                <p className="text-sm" style={{ color: 'var(--muted)' }}>
                  {faq.a}
                </p>
              </GlassyCard>
            ))}
          </div>
        </section>

        <GlassyDivider />

        {/* ==================== FINAL CTA ==================== */}
        <section className="animate-on-scroll py-20 px-4 transition-all duration-700 delay-700">
          <GlassyCard className="max-w-4xl mx-auto text-center p-12 rounded-3xl relative overflow-hidden">
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl"
              style={{
                background:
                  'radial-gradient(circle, var(--text) 0%, transparent 70%)',
                opacity: 0.03,
              }}
            />

            <div className="relative z-10">
              <motion.div
                animate={{
                  y: [0, -10, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <TfiRocket
                  className="w-16 h-16 mx-auto mb-4"
                  style={{ color: 'var(--text)' }}
                />
              </motion.div>
              <h2
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ color: 'var(--text)' }}
              >
                Ready to elevate your brand?
              </h2>
              <p
                className="mb-8 max-w-md mx-auto"
                style={{ color: 'var(--muted)' }}
              >
                Let's turn your vision into reality. I'm just one message away.
              </p>
              <p className="text-xs text-yellow-500 mb-4">
                ⚠️ Please check your spam folder if you don't see my reply!
              </p>
              <motion.a
                href="#hire-form"
                whileHover={{ scale: 1.03, gap: '0.75rem' }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full transition-all duration-300"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                Let's Get Started
                <FiArrowRight className="w-4 h-4" />
              </motion.a>
            </div>
          </GlassyCard>
        </section>

        <style jsx>{`
          @keyframes shimmer {
            0% {
              transform: translateX(-100%) rotate(12deg);
            }
            100% {
              transform: translateX(200%) rotate(12deg);
            }
          }
          .animate-fade-in {
            animation: fadeIn 0.3s ease-out forwards;
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}</style>
      </div>
    </>
  );
}

export default HireMe;
